import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmDialog from "./ConfirmDialog";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { getCurrentUser } from "../services/authService";
import {
  createClientNote,
  deleteClientNote,
  fetchClientNotes,
  fetchNoteRevisions,
  updateClientNote,
} from "../services/clientService";
import { formatDateTime } from "../services/datetime";
import { can } from "../services/permissions";
import { labelFor } from "../services/enumLabels";

const VISIBILITIES = ["team", "private", "sensitive"];

/** team / private / sensitive each read differently at a glance. */
const VISIBILITY_STYLES = {
  team: "bg-raised text-ink-2",
  private: "bg-warning-wash text-warning-text",
  sensitive: "bg-danger-wash text-danger-text",
};

/**
 * What staff wrote down about a client.
 *
 * ## Two rules the API enforces and this screen only reflects
 *
 * **Nothing is silently overwritten.** Correcting a note files the previous
 * body as a revision first, so `has_revisions` means there is earlier text to
 * read — and the button that reads it is the point of the whole mechanism.
 *
 * **A note this user may not read never arrives.** Visibility is applied in the
 * API's query, not here, so there is nothing for this component to hide: a
 * sensitive note is simply absent for anybody without `clients.view_sensitive`.
 * The sensitive option is likewise offered only to somebody who holds it, which
 * is a convenience — the API refuses it regardless of what the form sends.
 */
export default function ClientNotes({ clientId }) {
  const { t, i18n } = useTranslation();

  const [notes, setNotes] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [body, setBody] = useState("");
  const [visibility, setVisibility] = useState("team");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [revisionsFor, setRevisionsFor] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [revisionsLoading, setRevisionsLoading] = useState(false);

  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const user = getCurrentUser();
  const mayWrite = can(user, "clients.update");
  const maySensitive = can(user, "clients.view_sensitive");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { notes: rows, meta: pagination } = await fetchClientNotes(clientId, { page });
      setNotes(rows);
      setMeta(pagination);
    } catch (err) {
      setError(apiErrorMessage(err, t("clients.notes.loadFailed")));
      setNotes([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, page, t]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setEditing(null);
    setBody("");
    setVisibility("team");
    setFieldErrors({});
  };

  const startEdit = (note) => {
    setEditing(note);
    setBody(note.body ?? "");
    setVisibility(note.visibility ?? "team");
    setFieldErrors({});
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setFieldErrors({});

    try {
      if (editing) {
        await updateClientNote(clientId, editing.id, { body: body.trim(), visibility });
      } else {
        await createClientNote(clientId, { body: body.trim(), visibility });
      }

      resetForm();
      setPage(1);
      await load();
    } catch (err) {
      setFieldErrors(apiFieldErrors(err));
      setError(apiErrorMessage(err, t("clients.notes.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const openRevisions = async (note) => {
    setRevisionsFor(note);
    setRevisionsLoading(true);
    try {
      setRevisions(await fetchNoteRevisions(clientId, note.id));
    } catch (err) {
      setRevisions([]);
      setError(apiErrorMessage(err, t("clients.notes.revisionsFailed")));
    } finally {
      setRevisionsLoading(false);
    }
  };

  const confirmDelete = async () => {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteClientNote(clientId, deleting.id);
      setDeleting(null);
      await load();
    } catch (err) {
      setDeleteError(apiErrorMessage(err, t("clients.notes.deleteFailed")));
    } finally {
      setDeleteBusy(false);
    }
  };

  const offered = VISIBILITIES.filter((option) => option !== "sensitive" || maySensitive);

  return (
    <div className="space-y-4">
      {mayWrite && (
        <form onSubmit={submit} className="card space-y-3 p-4">
          <div>
            <label className="field-label" htmlFor="note-body">
              {editing ? t("clients.notes.editTitle") : t("clients.notes.addTitle")}
            </label>
            <textarea
              id="note-body"
              rows={3}
              className="field-input"
              placeholder={t("clients.notes.placeholder")}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            {fieldErrors.body && <p className="field-error">{fieldErrors.body[0]}</p>}
          </div>

          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <label className="field-label" htmlFor="note-visibility">
                {t("clients.notes.visibility")}
              </label>
              <select
                id="note-visibility"
                className="field-input"
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
              >
                {offered.map((option) => (
                  <option key={option} value={option}>
                    {labelFor("note_visibility", option)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-ink-2">
                {t(`clients.notes.visibilityHints.${visibility}`)}
              </p>
            </div>

            <div className="flex gap-2">
              {editing && (
                <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving}>
                  {t("common.cancel")}
                </button>
              )}
              <button type="submit" className="btn-primary" disabled={saving || !body.trim()}>
                {editing ? t("clients.notes.saveEdit") : t("clients.notes.add")}
              </button>
            </div>
          </div>

          {/* The whole reason an edit is not an overwrite. */}
          {editing && <p className="text-xs text-ink-2">{t("clients.notes.editHint")}</p>}
        </form>
      )}

      {error && <p className="field-error">{error}</p>}

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : notes.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("clients.notes.empty")}</p>
        ) : (
          <ul className="divide-y divide-line">
            {notes.map((note) => (
              <li key={note.id} className="px-4 py-3">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-1 text-caption ${
                      VISIBILITY_STYLES[note.visibility] ?? VISIBILITY_STYLES.team
                    }`}
                  >
                    {labelFor("note_visibility", note.visibility)}
                  </span>
                  <span className="text-xs text-ink-2">
                    {t("clients.notes.byline", {
                      author: note.author_name ?? t("clients.notes.unknownAuthor"),
                      when: formatDateTime(note.created_at, i18n.language),
                    })}
                  </span>
                  {note.edited_at && (
                    <span className="text-xs text-ink-2">
                      {t("clients.notes.edited", {
                        when: formatDateTime(note.edited_at, i18n.language),
                      })}
                    </span>
                  )}
                </div>

                <p className="whitespace-pre-wrap text-sm text-ink">{note.body}</p>

                <div className="mt-2 flex flex-wrap gap-3 text-sm">
                  {note.has_revisions && (
                    <button
                      type="button"
                      className="font-medium text-accent-text hover:underline"
                      onClick={() => openRevisions(note)}
                    >
                      {t("clients.notes.viewRevisions")}
                    </button>
                  )}
                  {mayWrite && (
                    <button
                      type="button"
                      className="font-medium text-accent-text hover:underline"
                      onClick={() => startEdit(note)}
                    >
                      {t("common.edit")}
                    </button>
                  )}
                  {mayWrite && (
                    <button
                      type="button"
                      className="font-medium text-danger-text hover:underline"
                      onClick={() => {
                        setDeleteError("");
                        setDeleting(note);
                      }}
                    >
                      {t("common.delete")}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {meta && meta.last_page > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t("common.previous")}
          </button>
          <span className="text-sm text-ink-2">
            {t("common.pageOf", { current: meta.current_page, last: meta.last_page })}
          </span>
          <button
            type="button"
            className="btn-secondary"
            disabled={page >= meta.last_page}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("common.next")}
          </button>
        </div>
      )}

      {/* What the note used to say — the evidence that nothing was overwritten. */}
      {revisionsFor && (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
          <div className="card my-8 w-full max-w-lg p-5">
            <h2 className="mb-1 text-lg font-bold">{t("clients.notes.revisionsTitle")}</h2>
            <p className="mb-4 text-sm text-ink-2">{t("clients.notes.revisionsSubtitle")}</p>

            {revisionsLoading ? (
              <p className="py-6 text-center text-sm text-ink-2">{t("common.loading")}</p>
            ) : revisions.length === 0 ? (
              <p className="py-6 text-center text-sm text-ink-2">
                {t("clients.notes.noRevisions")}
              </p>
            ) : (
              <ul className="space-y-3">
                {revisions.map((revision) => (
                  <li key={revision.id} className="rounded-lg border border-line p-3">
                    <p className="mb-1 text-xs text-ink-2">
                      {t("clients.notes.byline", {
                        author: revision.edited_by_name ?? t("clients.notes.unknownAuthor"),
                        when: formatDateTime(revision.created_at, i18n.language),
                      })}
                    </p>
                    <p className="whitespace-pre-wrap text-sm text-ink">{revision.body}</p>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setRevisionsFor(null);
                  setRevisions([]);
                }}
              >
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t("clients.notes.deleteTitle")}
        message={t("clients.notes.deleteMessage")}
        busy={deleteBusy}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}
